import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";

export default function MfaVerify() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const verify = async () => {
    if (!/^\d{6}$/.test(code.trim())) { toast.error("Enter the 6-digit code from your authenticator."); return; }
    setLoading(true);
    try {
      await api.post("/auth/mfa/verify", { code: code.trim() });
      toast.success("Verified.");
      const raw = sp.get("next") || "/admin";
      // Guard against open-redirect: only allow same-origin admin paths
      const safe = raw.startsWith("/admin") && !raw.startsWith("//") ? raw : "/admin";
      nav(safe);
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid code"); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto pt-24 px-6 text-center" data-testid="mfa-verify-page">
      <ShieldCheck className="w-10 h-10 text-amber-600 mx-auto" />
      <h1 className="mt-3 text-2xl font-bold text-slate-900">Two-factor required</h1>
      <p className="mt-2 text-sm text-slate-600">Enter the current 6-digit code from your authenticator app to continue.</p>
      <input
        data-testid="mfa-verify-input"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        onKeyDown={(e) => { if (e.key === "Enter") verify(); }}
        inputMode="numeric"
        autoFocus
        placeholder="123456"
        className="mt-6 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-2xl font-mono tracking-[0.5em] text-center focus:outline-none focus:border-slate-900"
      />
      <button
        data-testid="mfa-verify-btn"
        onClick={verify}
        disabled={loading || code.length !== 6}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 disabled:opacity-40"
      >
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying…</> : "Verify"}
      </button>
    </div>
  );
}
